/** 与后端 FormatInstantForAdminApi / ActivityRegEndUpperBoundUtc 一致 */

import { parseActivityInstantToUtcMs, tenantUiNowMs } from './activityCheckInDate'

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/** 报名开始：有配置则从该时刻起可报名；未配置则 null（不限制开始） */
export function activityRegStartMs(regStartDate: string): number | null {
  const t = regStartDate.trim()
  if (!t) return null
  if (DATE_ONLY.test(t)) {
    const ms = Date.parse(`${t}T00:00:00Z`)
    return Number.isFinite(ms) ? ms : null
  }
  return parseActivityInstantToUtcMs(t)
}

/** 报名截止上界（含）：与后端 ActivityRegEndUpperBoundUtc 一致 */
export function activityRegEndUpperBoundMs(regEndDate: string): number | null {
  const t = regEndDate.trim()
  if (!t) return null
  if (DATE_ONLY.test(t)) {
    const dayStart = Date.parse(`${t}T00:00:00Z`)
    if (!Number.isFinite(dayStart)) return null
    return dayStart + 86400000 - 1
  }
  const instant = parseActivityInstantToUtcMs(t)
  if (instant === null) {
    const fallback = Date.parse(t.includes('T') ? t : t.replace(' ', 'T'))
    return Number.isFinite(fallback) ? fallback : null
  }
  const d = new Date(instant)
  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0 &&
    !/T\d{2}:[1-9]/.test(t) &&
    !/ \d{2}:[1-9]/.test(t)
  ) {
    return instant + 86400000 - 1
  }
  return instant
}

/** 报名截止：有配置则该时刻后不可报名；未配置则 null（不限制截止） */
export function activityRegEndMs(regEndDate: string): number | null {
  return activityRegEndUpperBoundMs(regEndDate)
}

export function isActivityRegNotOpen(regStartDate: string, nowMs: number = tenantUiNowMs()): boolean {
  const ms = activityRegStartMs(regStartDate)
  return ms !== null && nowMs < ms
}

export function isActivityRegClosed(regEndDate: string, nowMs: number = tenantUiNowMs()): boolean {
  const upper = activityRegEndUpperBoundMs(regEndDate)
  return upper !== null && nowMs > upper
}

/** 活动是否已开始（到达 EventDate 时刻后视为已开始，与后端取消报名校验一致） */
export function isActivityStarted(eventDate: string, nowMs: number = tenantUiNowMs()): boolean {
  const t = eventDate.trim()
  if (!t) return false
  const ms = parseActivityInstantToUtcMs(t)
  return ms !== null && nowMs >= ms
}

/** 名额已满（与列表卡片 isFull 一致：status=full 或 报名人数达上限） */
export function isActivityAtCapacity(
  a: ActivityRegistrationAvailabilityInput,
): boolean {
  const st = String(a.status || '').toLowerCase()
  if (st === 'full') return true
  const cap = Number(a.capacity ?? 0)
  const enrolled = Number(a.enrolled ?? 0)
  return cap > 0 && enrolled >= cap
}

/** 已报满且活动未结束 */
export function isActivityFullNotEnded(a: ActivityRegistrationAvailabilityInput): boolean {
  if (String(a.status || '').toLowerCase() === 'ended') return false
  return isActivityAtCapacity(a)
}

export type ActivityRegistrationAvailabilityInput = {
  status?: string
  regStartDate?: string
  regEndDate?: string
  capacity?: number
  enrolled?: number
}

/** 会员端当前仍可报名：进行中、在报名窗口内、未满员 */
export function isActivityOpenForRegistration(
  a: ActivityRegistrationAvailabilityInput,
  nowMs: number = tenantUiNowMs(),
): boolean {
  const st = String(a.status || '').toLowerCase()
  if (st !== 'ongoing') return false
  if (isActivityRegNotOpen(a.regStartDate ?? '', nowMs)) return false
  if (isActivityRegClosed(a.regEndDate ?? '', nowMs)) return false
  if (isActivityAtCapacity(a)) return false
  return true
}

/** 报名已截止但活动尚未结束（与卡片「已截止」一致：未满员且报名窗口已关闭） */
export function isActivityRegistrationClosedNotEnded(
  a: ActivityRegistrationAvailabilityInput,
  nowMs: number = tenantUiNowMs(),
): boolean {
  return activityListFilterBucket(a, nowMs) === '已截止'
}

/** 活动列表 Tab 互斥分桶（与卡片按钮文案优先级一致，避免各 Tab 数量与列表不一致） */
export type ActivityListFilterBucket = '可报名' | '已截止' | '已报满' | '已结束'

export function activityListFilterBucket(
  a: ActivityRegistrationAvailabilityInput,
  nowMs: number = tenantUiNowMs(),
): ActivityListFilterBucket | null {
  const st = String(a.status || '').toLowerCase()
  if (st === 'ended') return '已结束'
  if (isActivityAtCapacity(a)) return '已报满'
  if (isActivityOpenForRegistration(a, nowMs)) return '可报名'
  if (isActivityRegClosed(a.regEndDate ?? '', nowMs)) return '已截止'
  return null
}

export function activityMatchesListFilter(
  a: ActivityRegistrationAvailabilityInput,
  filter: '全部' | ActivityListFilterBucket,
  nowMs: number = tenantUiNowMs(),
): boolean {
  if (filter === '全部') return true
  return activityListFilterBucket(a, nowMs) === filter
}

export function countActivitiesByListFilter(
  list: ActivityRegistrationAvailabilityInput[],
  nowMs: number = tenantUiNowMs(),
): Record<ActivityListFilterBucket, number> {
  const out: Record<ActivityListFilterBucket, number> = {
    可报名: 0,
    已截止: 0,
    已报满: 0,
    已结束: 0,
  }
  for (const a of list) {
    const bucket = activityListFilterBucket(a, nowMs)
    if (bucket) out[bucket] += 1
  }
  return out
}

/** 管理端活动列表 Tab（含草稿；「已截止」合并时间截止与已报满） */
export type AdminActivityListFilterKey = 'all' | 'open' | 'closed' | 'draft' | 'ended'

export function adminActivityMatchesListFilter(
  a: ActivityRegistrationAvailabilityInput & { status?: string },
  filter: AdminActivityListFilterKey,
  nowMs: number = tenantUiNowMs(),
): boolean {
  const st = String(a.status || '').toLowerCase()
  if (filter === 'all') return true
  if (filter === 'draft') return st === 'draft'
  if (filter === 'ended') return st === 'ended'
  if (st === 'draft' || st === 'ended') return false
  if (filter === 'open') return isActivityOpenForRegistration(a, nowMs)
  if (filter === 'closed') {
    if (isActivityOpenForRegistration(a, nowMs)) return false
    return isActivityAtCapacity(a) || isActivityRegClosed(a.regEndDate ?? '', nowMs)
  }
  return false
}

export function countAdminActivitiesByListFilter(
  list: (ActivityRegistrationAvailabilityInput & { status?: string })[],
  nowMs: number = tenantUiNowMs(),
): Record<Exclude<AdminActivityListFilterKey, 'all'>, number> {
  return {
    open: list.filter((a) => adminActivityMatchesListFilter(a, 'open', nowMs)).length,
    closed: list.filter((a) => adminActivityMatchesListFilter(a, 'closed', nowMs)).length,
    draft: list.filter((a) => adminActivityMatchesListFilter(a, 'draft', nowMs)).length,
    ended: list.filter((a) => adminActivityMatchesListFilter(a, 'ended', nowMs)).length,
  }
}
